import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { useDrawerSave } from './useDrawerSave';
import { IconArrowExport } from '@nxsys/components/AppIconSvg';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';

import {
  defaultExpenseClaimRuleOptions,
  expenseMilesOptions,
  expenseTypes,
} from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useApproveExpenseEmployee,
  useCreateExpenseEmployee,
  useGetExpensePotEmployee,
  useRejectExpenseEmployee,
  useSubmitExpenseEmployee,
  useUpdateSettingEmployeeById,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetExpenseEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import {
  CreateExpenseEmployeeParams,
  RejectExpenseEmployeeParams,
  SubmitExpenseEmployeeParams,
} from '@nxsys/types/api';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { IExpense } from '@nxsys/types/models/expense';
import { formatStatus, showCurrencyByCountry } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Col, Form, Row, Switch, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce, find, toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import RejectExpenseModal, {
  RejectExpenseValues,
} from './components/RejectExpenseModal';
import CreateExpenseModal, {
  CreateExpenseValues,
} from './components/CreateExpenseModal';
import SubmitExpenseModal, {
  SubmitExpenseValues,
} from './components/SubmitExpenseModal';
import ViewExpenseModal from './components/ViewExpenseModal';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';

type Props = {
  data: EmployeeRecordType | undefined;
  readOnly?: boolean;
};

const SettingsExpense = ({ data, readOnly }: Props) => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { payrollCountry, isEmployee, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const employeeId = isEmployee
    ? user?.subcontractorId?.id
    : (params?.employeeId ?? null);

  // Payroll settings
  const isPayrollSettingLoading = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.loading,
  );
  const payrollSettingData = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.data,
  );
  const getPayrollSettingByEmployeeId = useCallback(
    (id: string) => dispatch(employeeActions.getPayrollSettingByEmployeeId(id)),
    [dispatch],
  );
  const getEmployeeById = useCallback(
    (id: string) => dispatch(employeeActions.getEmployeeById(id)),
    [dispatch],
  );
  const { run: updateSettingEmployeeById, loading: isUpdating } =
    useUpdateSettingEmployeeById();

  // Expense pot
  const { value: expensePot, run: runExpensePot } = useGetExpensePotEmployee();
  const expensePotData = useMemo(() => expensePot?.data, [expensePot]);
  const getExpensePot = (id: string) => {
    runExpensePot(id, { page: 0, size: 1 });
  };

  // Expense list (from Expense.tsx pattern)
  const { expenseList } = useSelector((state: AppState) => state.employeeStore);
  const {
    loading: expenseListLoading,
    list,
    pagination,
    queryParams,
    originalData,
  } = expenseList;

  const [selectedRows, setSelectedRows] = useState<IExpense[]>([]);
  const [isRejectExpenseModalOpen, setIsRejectExpenseModalOpen] =
    useState(false);
  const [isCreateExpenseModalOpen, setIsCreateExpenseModalOpen] =
    useState(false);
  const [isOpenSubmitExpenseModal, setIsOpenSubmitExpenseModal] =
    useState(false);
  const [openViewExpenseModal, setOpenViewExpenseModal] = useState(false);

  const {
    loading: approveExpenseEmployeeLoading,
    run: approveExpenseEmployeeRun,
  } = useApproveExpenseEmployee();
  const {
    loading: rejectExpenseEmployeeLoading,
    run: rejectExpenseEmployeeRun,
  } = useRejectExpenseEmployee();
  const { run: createExpenseEmployeeRun } = useCreateExpenseEmployee();
  const { run: submitExpenseRun } = useSubmitExpenseEmployee();

  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);

  useEffect(() => {
    if (employeeId) {
      getPayrollSettingByEmployeeId(employeeId);
      getExpensePot(employeeId);
      handleFetchData({ page: 0, size: 20 });
    }
  }, [employeeId]);

  useEffect(() => {
    if (payrollSettingData) {
      form.setFieldsValue({
        expenseClaimRule:
          defaultExpenseClaimRuleOptions.find(
            (option) => option.value === payrollSettingData?.expenseClaimRule,
          ) || null,
        isRequiredAttachmentExpenses:
          payrollSettingData?.isRequiredAttachmentExpenses ?? false,
      });
    }
  }, [payrollSettingData]);

  // Expense table logic (from Expense.tsx)
  const handleFetchData = (params: GetExpenseEmployeeListParams) => {
    if (!employeeId) return;
    const newParams = { ...params, id: employeeId };
    dispatch(employeeActions.getExpenseEmployeeList(newParams));
  };

  const refetch = () => handleFetchData({ ...queryParams });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({ ...queryParams, page: 0, size: 20, search });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedFetchData(e.target.value);
  };

  const columns: ColumnsType<IExpense> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <Tooltip title="Download">
            <li onClick={() => window.open(record.document, '_blank')}>
              <Icons.IconDownload />
            </li>
          </Tooltip>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'status',
      title: messages['common.status'],
      dataIndex: 'status',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
          {formatStatus(value)}
        </AppTagStatus>
      ),
    },
    {
      key: 'expenseClaimDate',
      title: messages['common.date'] ?? 'Date',
      dataIndex: 'date',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'expenseType',
      title: messages['common.type'] ?? 'Type',
      dataIndex: 'type',
      render: (value) =>
        expenseTypes[value as keyof typeof expenseTypes] || 'Mileage',
    },
    {
      key: 'expenseName',
      title: messages['common.expenseName'],
      render: (record) =>
        record?.type
          ? find(expenseMilesOptions, (item) => item.value === record?.type)
              ?.label || 'Opening Balance'
          : record?.expense?.expenseName || '_',
    },
    {
      key: 'claimAmount',
      title: messages['common.amount'],
      dataIndex: 'claimAmount',
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
  ];

  const handleExport = (data: IExpense[]) => {
    const dataExport = data.map((item) => ({
      status: formatStatus(item?.status),
      id: item?.specialId,
      date: item?.date
        ? moment(item?.date).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      expenseType: !item?.type || item?.type === 'other' ? 'Other' : 'Mileage',
      expenseName: item?.type
        ? find(expenseMilesOptions, (u) => u.value === item?.type)?.label ||
          item?.expense?.expenseName ||
          ''
        : item?.expense?.expenseName || '',
      description: item?.description || '',
      amountClaim: formatMoney(
        item?.claimAmount || 0,
        showCurrencyByCountry(payrollCountry),
      ),
    }));
    const headers = [
      'Status',
      'Expense ID',
      'Date',
      'Expense Type',
      'Expense Name',
      'Description',
      'Amount',
    ];
    const exportName = csvConfigExport('Expense', headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    handleExport(selectedRows);
  };

  const handleApproveSelected = async () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    const res = await approveExpenseEmployeeRun({
      ids: selectedRows.map((item) => item.id),
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.approveSuccess'],
      });
      refetch();
    }
  };

  const handleRejectSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    setIsRejectExpenseModalOpen(true);
  };

  const handleReject = async (values: RejectExpenseValues) => {
    const rejectParams: RejectExpenseEmployeeParams = {
      ids: selectedRows.map((item) => item.id),
      reason: values.reason,
    };
    const res = await rejectExpenseEmployeeRun(rejectParams);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.rejectSuccess'],
      });
      refetch();
    }
  };

  const handleCreateExpense = async (values: CreateExpenseValues) => {
    if (!employeeId) return;
    const createParams: CreateExpenseEmployeeParams = {
      date: values.date?.toISOString() || null,
      document: null,
      description: values.description || '',
      taxPeriod: values.taxPeriod || null,
      claimAmount: toNumber(values.claimAmount) || 0,
      type: values.type?.value || null,
    };
    const isUploadFile = !!(values.document as File)?.name;
    if (isUploadFile) {
      const res = await dispatch(
        commonActions.uploadFileToS3({ file: values.document as File }),
      ).unwrap();
      createParams.document = res.url;
    }
    const res = await createExpenseEmployeeRun({
      ...createParams,
      id: employeeId,
    });
    if (res.success) {
      showMessage({ type: 'success', content: messages['common.addSuccess'] });
      refetch();
      setIsCreateExpenseModalOpen(false);
    }
  };

  const handleSubmitExpense = async (values: SubmitExpenseValues) => {
    if (!employeeId) return;
    const submitParams: SubmitExpenseEmployeeParams = {
      data: [
        {
          date: values.date?.toISOString() || '',
          openingBalance: toNumber(values.openingBalance),
          subcontractorId: employeeId,
        },
      ],
    };
    await submitExpenseRun(submitParams);
    showMessage({ type: 'success', content: messages['common.submitSuccess'] });
    getExpensePot(employeeId);
  };

  const handleSaveSettings = async () => {
    if (!employeeId) return;
    const values = form.getFieldsValue(true);
    await updateSettingEmployeeById(employeeId, {
      expenseClaimRule: values.expenseClaimRule?.value || null,
      isRequiredAttachmentExpenses: values.isRequiredAttachmentExpenses ?? null,
    } as any);
    getPayrollSettingByEmployeeId(employeeId);
    getEmployeeById(employeeId);
    showMessage({ type: 'success', content: messages['common.updateSuccess'] });
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header.
  const inDrawer = useDrawerSave(handleSaveSettings, isUpdating, !readOnly);

  if (!payrollSettingData || isPayrollSettingLoading)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  const balance =
    expensePotData?.reduce((acc, curr) => acc + curr.closingBalance, 0) || 0;

  return (
    <div>
      {(isUpdating ||
        approveExpenseEmployeeLoading ||
        rejectExpenseEmployeeLoading) && <AppLoader />}
      {/* Form section - centered narrow width */}
      <div className="max-w-[472px] mx-auto">
        <Typography.Title
          level={5}
          className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
        >
          {messages['common.expenseClaimRule']}
        </Typography.Title>

        <AppFormContainer
          form={form}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          disabled={readOnly}
        >
          {/* Submitted / Paid */}
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item label={messages['common.submitted'] ?? 'Submitted'}>
                <AppInput
                  disabled
                  prefix={showCurrencyByCountry(payrollCountry)}
                  value={formatMoney(
                    expensePotData?.reduce(
                      (acc, curr) => acc + (curr.claimed || 0),
                      0,
                    ) || 0,
                    '',
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={messages['common.paid']}>
                <AppInput
                  disabled
                  prefix={showCurrencyByCountry(payrollCountry)}
                  value={formatMoney(
                    expensePotData?.reduce(
                      (acc, curr) => acc + (curr.paid || 0),
                      0,
                    ) || 0,
                    '',
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Balance */}
          <Form.Item label={messages['common.balance']}>
            <AppInput
              disabled
              prefix={showCurrencyByCountry(payrollCountry)}
              value={formatMoney(balance, '')}
            />
          </Form.Item>

          {/* Expense Claim Rule */}
          <Form.Item
            label={messages['common.expenseClaimRule']}
            name="expenseClaimRule"
          >
            <AppSelect
              options={defaultExpenseClaimRuleOptions}
              className="w-full !min-w-0"
              placeholder={messages['form.select']}
            />
          </Form.Item>

          {/* Expenses-Receipt upload mandatory? */}
          <Form.Item
            name="isRequiredAttachmentExpenses"
            valuePropName="checked"
          >
            <div className="flex items-center justify-between">
              <span>{messages['common.expenseReceiptUploadMandatory']}</span>
              <Switch
                checked={watchValues?.isRequiredAttachmentExpenses ?? false}
                onChange={(checked) =>
                  form.setFieldValue('isRequiredAttachmentExpenses', checked)
                }
              />
            </div>
          </Form.Item>
        </AppFormContainer>

        {/* Action Buttons */}
        {!readOnly && !inDrawer && (
          <div className="flex items-center justify-start pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={handleSaveSettings}
              loading={isUpdating}
            />
          </div>
        )}

        {/* Divider */}
        <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />

        {/* Expenses Claimed YTD */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider">
              {messages['common.expensesClaimedYTD'] ?? 'Expenses Claimed YTD'}
            </Typography.Text>
            <div
              className="flex items-center gap-1 cursor-pointer bg-[#F1F1F1] rounded-[4px] h-[24px] px-[6px]"
              onClick={() => setOpenViewExpenseModal(true)}
            >
              <Icons.IconTimePast width={14} height={14} color="#444444" />
              <span className="text-[12px] leading-[1.67] text-[#000000]">
                {messages['common.history'] ?? 'History'}
              </span>
            </div>
          </div>
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <div className="mb-4">
                <div className="text-[14px] text-[#6F767E] mb-1">
                  {messages['common.carVanMilesClaimed']}
                </div>
                <AppInput
                  disabled
                  value={formatMoney(
                    originalData?.milesYTDs?.carOrVan || 0,
                    showCurrencyByCountry(payrollCountry),
                  )}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="mb-4">
                <div className="text-[14px] text-[#6F767E] mb-1">
                  {messages['common.motorcycleMilesClaimed']}
                </div>
                <AppInput
                  disabled
                  value={formatMoney(
                    originalData?.milesYTDs?.motorcycle || 0,
                    showCurrencyByCountry(payrollCountry),
                  )}
                />
              </div>
            </Col>
          </Row>
          <div className="mb-4">
            <div className="text-[14px] text-[#6F767E] mb-1">
              {messages['common.electricCarMilesClaimed']}
            </div>
            <AppInput
              disabled
              value={formatMoney(
                originalData?.milesYTDs?.electricCar || 0,
                showCurrencyByCountry(payrollCountry),
              )}
            />
          </div>
        </div>
      </div>
      {/* end narrow form wrapper */}

      {/* Expense Table - full width */}
      <div className="w-full mt-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <AppInputSearch onChange={handleSearchChange} />
          </div>
          <div className="flex items-center gap-2">
            <AppButton
              variant="outlined"
              name={messages['common.export']}
              leftIcon={<IconArrowExport />}
              onClick={handleExportSelected}
            />
            <AppButton
              variant="outlined"
              name={messages['common.reject']}
              onClick={handleRejectSelected}
            />
            <AppButton
              variant="outlined"
              name={messages['common.approve']}
              onClick={handleApproveSelected}
            />
            <AppButton
              type="primary"
              name={messages['common.addNew']}
              onClick={() => setIsCreateExpenseModalOpen(true)}
              leftIcon={<PlusOutlined />}
            />
          </div>
        </div>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={expenseListLoading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1200, y: 500 }}
            titleEmpty={messages['attachmentOrder.empty']}
            columns={columns}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
          />
        </AppsContent>
      </div>

      {/* Modals */}
      <RejectExpenseModal
        isOpen={isRejectExpenseModalOpen}
        toggle={() => setIsRejectExpenseModalOpen(false)}
        onSubmit={handleReject}
      />
      <CreateExpenseModal
        isOpen={isCreateExpenseModalOpen}
        toggle={() => setIsCreateExpenseModalOpen(false)}
        onSubmit={handleCreateExpense}
        companyId={data?.companyPayment?.id ?? ''}
        employeeId={employeeId!}
      />
      <SubmitExpenseModal
        isOpen={isOpenSubmitExpenseModal}
        toggle={() => setIsOpenSubmitExpenseModal(false)}
        onSubmit={handleSubmitExpense}
      />
      <ViewExpenseModal
        isOpen={openViewExpenseModal}
        toggle={() => setOpenViewExpenseModal(false)}
        employeeId={employeeId || ''}
      />
    </div>
  );
};

export default SettingsExpense;
